import React, { useState, useEffect, useRef } from 'react';
import { SurveyData, User, UserRole, DynamicQuestion, SurveySection } from '../types';
import { Plus, Trash2, Save, User as UserIcon, ArrowRight, ArrowLeft, Download, Loader, Camera, Image as ImageIcon, X, Sliders, Check } from 'lucide-react';
import { initStorage, saveSurvey, getTemplateByType, getDynamicQuestions, getNextSerialNo, getDynamicSections, getCategoryForAge, getCategoryName, getMemberAge, getMemberName, getMemberPhone, getMemberIICStatus, getMemberPhoto, getSurveyIICStatus, getHouseholdName, getHouseholdPhone, getHouseholdSerialNo, getHouseholdPhoto, getSurveyIICId } from '../services/storage';
import { drawCardOnCanvas, downloadCanvas, formatSerialNoForDisplay } from '../utils/cardUtils';
import { useToast } from './Toast';
import { Printer } from 'lucide-react';
import PhotoCropper from './PhotoCropper';

const PRINT_STYLES = `
  @media print {
    body * {
      visibility: hidden !important;
    }
    #registration-receipt, #registration-receipt * {
      visibility: visible !important;
    }
    #registration-receipt {
      position: fixed !important;
      left: 0 !important;
      top: 0 !important;
      width: 150% !important;
      max-width: 150% !important;
      height: auto !important;
      border: none !important;
      box-shadow: none !important;
      margin: 0 !important;
      padding: 40px !important;
      background: white !important;
      z-index: 9999 !important;
    }
    .no-print {
      display: none !important;
    }
  }
`;

interface MemberCardCanvasProps {
  cardTemplate: any;
  member: any;
  parentData: Record<string, any>;
  index: number;
  questions: any[];
  onDownload: () => void;
}

const MemberCardCanvas: React.FC<MemberCardCanvasProps> = ({ cardTemplate, member, parentData, index, questions, onDownload }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [computedCategoryName, setComputedCategoryName] = useState('');
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    if (canvasRef.current && cardTemplate) {
      const age = getMemberAge(member, questions);
      const name = getMemberName(member, questions);
      const phone = getMemberPhone(member, questions) || getHouseholdPhone(parentData, questions);
      const photo = getMemberPhoto(member, questions) || getHouseholdPhoto(parentData, questions);

      setDisplayName(name || `Entry ${index + 1}`);

      const category = getCategoryForAge(age);
      const categoryName = getCategoryName(category);
      setComputedCategoryName(categoryName);
      const draftSerialNo = category ? `${category}-DRAFT-${(index + 1).toString().padStart(2, '0')}` : `DRAFT-${index + 1}`;

      const mergedData = {
        ...parentData,
        ...member,
        name,
        headName: name,
        phone,
        housePhoto: photo,
        categoryName: member.categoryName || categoryName,
        serialNo: member.serialNo || member.idNo || draftSerialNo,
        idNo: member.serialNo || member.idNo || draftSerialNo,
        id_no: member.serialNo || member.idNo || draftSerialNo,
        id: member.serialNo || member.idNo || draftSerialNo,
      };
      
      drawCardOnCanvas(canvasRef.current, cardTemplate, mergedData);
    }
  }, [cardTemplate, member, parentData, index, questions]);

  const handleDownload = () => {
    if (canvasRef.current) {
      const name = getMemberName(member, questions) || `Member_${index + 1}`;
      const filename = `id_card_${name.replace(/\s+/g, '_')}.png`;
      downloadCanvas(canvasRef.current, filename);
      onDownload();
    }
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow border border-emerald-150 flex flex-col items-center space-y-3 w-full max-w-md">
      <div className="w-full text-center border-b pb-2">
        <h4 className="font-bold text-emerald-800 text-sm">
          ID Card: {displayName}
        </h4>
        {computedCategoryName && (
          <span className="inline-block mt-1 px-3 py-0.5 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-150">
            Category: {computedCategoryName}
          </span>
        )}
      </div>
      <canvas ref={canvasRef} className="w-full h-auto rounded border shadow-inner bg-gray-50" />
      <button 
        type="button" 
        onClick={handleDownload} 
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-lg text-sm flex justify-center items-center shadow transition-colors"
      >
        <Download className="mr-1.5 w-4 h-4"/> Download ID Card
      </button>
    </div>
  );
};

interface SurveyFormProps {
  user: User;
  onSuccess: () => void;
  initialData?: SurveyData | null;
}

const compressImageForDb = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        let width = img.width;
        let height = img.height;
        const MAX_DIM = 800;
        
        if (width > height) {
            if (width > MAX_DIM) {
                height *= MAX_DIM / width;
                width = MAX_DIM;
            }
        } else {
            if (height > MAX_DIM) {
                width *= MAX_DIM / height;
                height = MAX_DIM;
            }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        if(!ctx) { reject(new Error("Canvas failed")); return; }
        
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.7;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        
        while (dataUrl.length > 270000 && quality > 0.2) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(dataUrl);
      };
      img.onerror = (e) => reject(e);
    };
    reader.onerror = (e) => reject(e);
  });
};

const QuestionCard: React.FC<{ label: string, required?: boolean, isAdmin?: boolean, children: React.ReactNode }> = ({ label, required, isAdmin, children }) => (
  <div className="bg-white border border-gray-150 rounded-lg p-6 shadow-sm mb-4 hover:shadow-md transition-shadow border-l-4 border-l-transparent hover:border-l-blue-500 w-full block animate-in fade-in zoom-in-95 duration-150">
    <label className="block text-base font-bold text-gray-800 mb-3 w-full">
      {label} {required && <span className="text-red-500">*</span>}
      {required && isAdmin && <span className="text-xs font-normal text-emerald-600 ml-2 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-150">(Admin: Can Skip)</span>}
    </label>
    <div className="w-full block">{children}</div>
  </div>
);

const ImageInput = ({ value, onChange, onProcessingChange, photoConfig }: { value: string | undefined, onChange: (val: string) => void, onProcessingChange: (status: boolean) => void, photoConfig?: any }) => {
  const [processing, setProcessing] = useState(false);
  const [rawImage, setRawImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setProcessing(true);
    onProcessingChange(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        setRawImage(e.target?.result as string);
        setShowCropper(true);
        setProcessing(false);
        onProcessingChange(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      alert("Error reading file.");
      setProcessing(false);
      onProcessingChange(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const clearImage = () => {
    onChange('');
    setRawImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  // Aspect ratio from admin config or default 3:4 (common ID card photo)
  const aspect = photoConfig ? photoConfig.width / photoConfig.height : 0.75;

  return (
    <div className="w-full">
       <input 
         type="file" 
         accept="image/*" 
         capture="environment"
         className="hidden" 
         ref={fileInputRef}
         onChange={handleFileChange}
       />
       <input 
         type="file" 
         accept="image/*" 
         className="hidden" 
         ref={galleryInputRef}
         onChange={handleFileChange}
       />
       
       {!value && (
         <div className="grid grid-cols-2 gap-3 w-full">
            <button 
                type="button" 
                onClick={() => fileInputRef.current?.click()}
                disabled={processing}
                className="h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-600 transition-all active:scale-95"
            >
                {processing ? <Loader className="animate-spin mb-2" /> : <Camera size={32} className="mb-2" />}
                <span className="font-bold text-sm">Take Photo</span>
            </button>
            <button 
                type="button" 
                onClick={() => galleryInputRef.current?.click()}
                disabled={processing}
                className="h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 transition-all active:scale-95"
            >
                {processing ? <Loader className="animate-spin mb-2" /> : <ImageIcon size={32} className="mb-2" />}
                <span className="font-bold text-sm">Choose File</span>
            </button>
         </div>
       )}

       {value && (
         <div className="relative w-full bg-white border border-gray-150 rounded-xl overflow-hidden shadow-sm flex flex-col items-center p-3 animate-in fade-in slide-in-from-bottom-2">
            <div className="w-full aspect-[4/5] max-w-[240px] relative rounded-lg overflow-hidden border border-gray-150 shadow-inner">
               <img src={value} alt="Uploaded" className="w-full h-full object-cover" />
            </div>
            
            <div className="w-full flex justify-between items-center mt-4">
               <div className="flex items-center text-emerald-600 font-bold text-xs bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-150">
                  <Check size={14} className="mr-1.5"/> Photo Ready
               </div>
               <div className="flex gap-2">
                  <button 
                    type="button"
                    onClick={() => {
                        setRawImage(value);
                        setShowCropper(true);
                    }}
                    className="p-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-150 transition-colors"
                    title="Edit/Re-crop"
                  >
                    <Sliders size={18} />
                  </button>
                  <button 
                      type="button" 
                      onClick={clearImage}
                      className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-xs hover:bg-red-150 font-black uppercase tracking-wider transition-colors"
                  >
                      Remove
                  </button>
               </div>
            </div>
         </div>
       )}

       {showCropper && rawImage && (
         <PhotoCropper 
           image={rawImage}
           aspect={aspect}
           onCropComplete={(cropped) => {
             onChange(cropped);
             setShowCropper(false);
           }}
           onCancel={() => setShowCropper(false)}
         />
       )}
    </div>
  );
};

const YesNoInput = ({ value, onChange }: { value: boolean | string | undefined, onChange: (val: any) => void }) => {
  const isYes = value === true || value === 'Yes' || value === 'true';
  const isNo = value === false || value === 'No' || value === 'false';
  return (
    <div className="flex gap-4 w-full">
      <button type="button" onClick={() => onChange(true)} className={`flex-1 py-3 px-4 rounded-lg border-2 flex items-center justify-center space-x-2 transition-all ${isYes ? 'border-emerald-500 bg-emerald-600 text-white' : 'border-gray-150 bg-white text-gray-600 hover:border-emerald-300'}`}>
        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isYes ? 'border-white bg-white text-emerald-600' : 'border-gray-300'}`}>{isYes && <div className="w-2.5 h-2.5 rounded-full bg-emerald-600" />}</div><span className="font-bold">Yes / ഉണ്ട്</span>
      </button>
      <button type="button" onClick={() => onChange(false)} className={`flex-1 py-3 px-4 rounded-lg border-2 flex items-center justify-center space-x-2 transition-all ${isNo ? 'border-red-500 bg-red-600 text-white' : 'border-gray-150 bg-white text-gray-600 hover:border-red-300'}`}>
        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isNo ? 'border-white bg-white text-red-600' : 'border-gray-300'}`}>{isNo && <div className="w-2.5 h-2.5 rounded-full bg-red-600" />}</div><span className="font-bold">No / ഇല്ല</span>
      </button>
    </div>
  );
};

const MultiSelect = ({ options, value, onChange }: { options: string[], value: string[], onChange: (v: string[]) => void }) => {
  const selected = value || [];
  const toggle = (opt: string) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {options.map(opt => (
        <label key={opt} className={`flex items-center space-x-3 p-3 rounded cursor-pointer border transition-all ${selected.includes(opt) ? 'bg-emerald-50 border-emerald-500' : 'bg-white border-gray-150 hover:border-emerald-300'}`}>
           <input type="checkbox" className="w-5 h-5 accent-emerald-600" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
           <span className="text-gray-800 font-medium">{opt}</span>
        </label>
      ))}
    </div>
  )
}

const InputClass = "w-full border border-gray-300 p-3 rounded bg-white text-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none block";

const generateUniqueId = () => {
  return Date.now().toString() + Math.random().toString(36).substring(2, 9);
};

const SurveyForm: React.FC<SurveyFormProps> = ({ user, onSuccess, initialData }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const { addToast } = useToast();
  const [isMediaProcessing, setIsMediaProcessing] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cardTemplate, setCardTemplate] = useState<any>(null);

  useEffect(() => {
    const init = async () => {
      try {
        await initStorage().catch(err => console.warn("Init storage warning in SurveyForm:", err));
        const [qs, secs, tmpl] = await Promise.all([getDynamicQuestions(), getDynamicSections(), getTemplateByType('student')]);
        setQuestions(qs);
        setSections(secs);
        setCardTemplate(tmpl);

        if (initialData) {
          const loadedData = { ...(initialData as Record<string, any>) };
          setFormData(loadedData);
        } else {
          const nextSerial = await getNextSerialNo();
          const firstSection = secs.find(s => !s.isRepeater)?.id || 'sectionA';
          setFormData({
            [firstSection]: { serialNo: nextSerial },
            ...secs.filter(s => s.isRepeater).reduce<Record<string, any>>((acc, s) => ({ ...acc, [s.id]: [{ id: generateUniqueId() }] }), {})
          });
        }
      } catch (e) {
        console.error("Init failed", e);
        addToast("Failed to initialize form", "error");
      } finally {
        setInitLoading(false);
      }
    };
    init();
  }, [initialData]);

  useEffect(() => {
    if (step === sections.length && canvasRef.current && cardTemplate) {
      const allData = Object.values(formData).reduce<Record<string, any>>((acc, curr) => {
         if (typeof curr === 'object' && curr !== null && !Array.isArray(curr)) {
             return { ...acc, ...(curr as Record<string, any>) };
         }
         return acc;
      }, {});
      drawCardOnCanvas(canvasRef.current, cardTemplate, allData);
    }
  }, [step, formData, cardTemplate, sections]);

  const isVisible = (q: DynamicQuestion, data: Record<string, any>) => {
    if (!q.dependsOn) return true;
    const parentVal = data[q.dependsOn];
    if (q.visibleIf === undefined) return !!parentVal;
    if (typeof q.visibleIf === 'boolean') {
      return parentVal === q.visibleIf || parentVal === String(q.visibleIf);
    }
    if (Array.isArray(q.visibleIf)) {
      return q.visibleIf.includes(parentVal);
    }
    return parentVal === q.visibleIf;
  };

  const renderQuestions = (sectionId: string, data: Record<string, any>, updater: (k: string, v: any) => void) => {
    return questions
      .filter(q => q.section === sectionId)
      .filter(q => isVisible(q, data))
      .map(q => (
        <QuestionCard key={q.id} label={q.label} required={q.required} isAdmin={user.role === UserRole.MASTER_ADMIN}>
          {q.id === 'serialNo' ? (
             <input 
               className={`${InputClass} ${user.role !== UserRole.MASTER_ADMIN ? 'bg-gray-150 text-gray-500' : 'bg-white'}`} 
               value={data[q.id] || ''} 
               onChange={e => user.role === UserRole.MASTER_ADMIN && updater(q.id, e.target.value)}
               readOnly={user.role !== UserRole.MASTER_ADMIN} 
             />
          ) : q.type === 'yesno' ? (
            <YesNoInput value={data[q.id]} onChange={v => updater(q.id, v)} />
          ) : q.type === 'image' ? (
            <ImageInput 
                value={data[q.id]} 
                onChange={v => updater(q.id, v)} 
                onProcessingChange={setIsMediaProcessing}
                photoConfig={cardTemplate?.photoConfig}
            />
          ) : q.type === 'date' ? (
            <input 
              type="date"
              className={InputClass}
              value={data[q.id] || ''} 
              onChange={e => updater(q.id, e.target.value)}
            />
          ) : q.type === 'dropdown' ? (
            <select className={InputClass} value={data[q.id] || ''} onChange={e => updater(q.id, e.target.value)}>
              <option value="">Select Option</option>
              {q.options?.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : q.type === 'multiselect' ? (
             <MultiSelect options={q.options || []} value={data[q.id]} onChange={v => updater(q.id, v)} />
          ) : q.type === 'textarea' ? (
             <textarea className={InputClass} rows={3} value={data[q.id] || ''} onChange={e => updater(q.id, e.target.value)} />
          ) : (
             <input 
               type={q.id === 'phone' ? 'text' : q.type} 
               className={InputClass} 
               value={data[q.id] || ''} 
               onChange={e => {
                   if (q.id === 'phone') {
                       updater(q.id, e.target.value);
                   } else {
                       updater(q.id, q.type === 'number' && e.target.value !== '' ? parseFloat(e.target.value) : e.target.value);
                   }
               }} 
             />
          )}
        </QuestionCard>
      ));
  };

  const getSectionErrors = (sectionIdx: number): string[] => {
    // Master admins can skip mandatory questions
    if (user.role === UserRole.MASTER_ADMIN) {
      return [];
    }

    const section = sections[sectionIdx];
    if (!section) return [];

    const sectionQs = questions.filter(q => q.section === section.id);
    const errors: string[] = [];

    if (section.isRepeater) {
      const items = formData[section.id] || [];
      if (items.length === 0) {
        errors.push(`At least one entry is required for ${section.label}`);
      } else {
        items.forEach((item: any, idx: number) => {
          sectionQs.forEach(q => {
            if (isVisible(q, item) && q.required) {
              const val = item[q.id];
              const isEmpty = val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
              if (isEmpty) {
                errors.push(`${section.label} (Entry ${idx + 1}): ${q.label} is required`);
              }
            }
          });
        });
      }
    } else {
      const data = formData[section.id] || {};
      sectionQs.forEach(q => {
        if (isVisible(q, data) && q.required) {
          const val = data[q.id];
          const isEmpty = val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
          if (isEmpty) {
            errors.push(`${q.label} is required`);
          }
        }
      });
    }

    return errors;
  };

  const handleDownloadCard = () => {
    if (canvasRef.current) {
       try {
           const filename = `id_card_${getHouseholdSerialNo(formData) || 'draft'}.png`;
           downloadCanvas(canvasRef.current, filename);
           setHasDownloaded(true);
       } catch (e) {
           console.error(e);
           if (confirm("Download failed (possibly due to browser settings). Do you want to mark it as downloaded anyway so you can submit?")) {
               setHasDownloaded(true);
           }
       }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isMediaProcessing) return;

    const errors = getSectionErrors(step);
    if (errors.length > 0) {
      addToast(errors[0], "error");
      return;
    }

    const qualifyingMembers = getQualifyingMembers();
    if (qualifyingMembers.length === 0) {
      addToast("Empty submissions are not allowed. Please register at least one student.", "error");
      return;
    }

    setLoading(true);
    const studentCount = qualifyingMembers.length;
    const isMember = getSurveyIICStatus(formData, questions, sections);
    const paymentAmount = studentCount * (isMember ? 150 : 200);
    const surveyData: SurveyData = {
      id: initialData ? initialData.id : generateUniqueId(),
      submittedBy: user.id,
      submittedAt: new Date().toISOString(),
      paymentStatus: initialData?.paymentStatus || 'Pending',
      paymentAmount: paymentAmount,
      ...formData
    };
    try {
      await saveSurvey(surveyData);
      addToast('Registration submitted successfully!', 'success');
      setSubmitted(true);
    } catch (e: any) {
      console.error(e);
      if (e.message && e.message.includes('exceeds the maximum size')) {
         addToast('Failed: Images are too large. Please remove some photos or try again.', 'error');
      } else {
         addToast('Failed to save survey. Please check connection.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (isMediaProcessing) {
      addToast("Please wait for image to finish processing", "info");
      return;
    }

    const errors = getSectionErrors(step);
    if (errors.length > 0) {
      addToast(errors[0], "error");
      return;
    }

    const nextStepIdx = step + 1;
    if (nextStepIdx < sections.length) {
       const nextSec = sections[nextStepIdx];
       if (nextSec.isRepeater) {
           let maxLen = 0;
           for (let i = 0; i < nextStepIdx; i++) {
               if (sections[i].isRepeater) {
                   const len = (formData[sections[i].id] || []).length;
                   if (len > maxLen) maxLen = len;
               }
           }
           const currentLen = (formData[nextSec.id] || []).length;
           if (maxLen > currentLen) {
               setFormData(prev => {
                   const items = [...(prev[nextSec.id] || [])];
                   for (let i = currentLen; i < maxLen; i++) {
                       items.push({ id: generateUniqueId() });
                   }
                   return { ...prev, [nextSec.id]: items };
               });
           }
       }
    }
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getEntryTitle = (idx: number, currentSectionId: string) => {
    const nameQuestions = questions.filter(q => 
      q.label.toLowerCase().includes('name') || q.label.includes('പേര്') || q.label.toLowerCase().includes('student')
    );
    const currentItem = (formData[currentSectionId] || [])[idx];
    if (currentItem) {
        for (const nq of nameQuestions.filter(q => q.section === currentSectionId)) {
           if (currentItem[nq.id] && typeof currentItem[nq.id] === 'string' && currentItem[nq.id].trim() !== '') {
               return currentItem[nq.id];
           }
        }
    }
    for (const s of sections.filter(sec => sec.isRepeater)) {
        const otherItem = (formData[s.id] || [])[idx];
        if (otherItem) {
           for (const nq of nameQuestions.filter(q => q.section === s.id)) {
              if (otherItem[nq.id] && typeof otherItem[nq.id] === 'string' && otherItem[nq.id].trim() !== '') {
                  return otherItem[nq.id];
              }
           }
        }
    }
    return `Entry ${idx + 1}`;
  };

  const updateSectionData = (sectionId: string, key: string, val: any) => {
    const rawData = formData[sectionId];
    const currentSectionData = (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) ? (rawData as Record<string, any>) : {};
    setFormData({
      ...formData,
      [sectionId]: { ...currentSectionData, [key]: val }
    });
  };

  const updateRepeaterItem = (sectionId: string, idx: number, key: string, val: any) => {
    const list = formData[sectionId];
    const items = Array.isArray(list) ? [...list] : [];
    const rawItem = items[idx];
    const currentItem = (rawItem && typeof rawItem === 'object') ? (rawItem as Record<string, any>) : {};
    items[idx] = { ...currentItem, [key]: val };
    setFormData({ ...formData, [sectionId]: items });
  };
  
  const addRepeaterItem = (sectionId: string) => {
    const items = [...(formData[sectionId] || [])];
    items.push({ id: generateUniqueId() });
    setFormData({ ...formData, [sectionId]: items });
  };

  const removeRepeaterItem = (sectionId: string, idx: number) => {
    const items = [...(formData[sectionId] || [])];
    setFormData({ ...formData, [sectionId]: items.filter((_, i) => i !== idx) });
  };

  const getQualifyingMembers = () => {
    let allStudents: any[] = [];
    
    // Check if a member is a parent, spouse, or non-student adult
    const isAdultOrNonStudent = (member: any): boolean => {
      if (!member || typeof member !== 'object') return true;
      for (const key of Object.keys(member)) {
        const val = member[key];
        if (typeof val === 'string' && val.trim().length > 0) {
          const v = val.toLowerCase().trim();
          const isParentOrSpouseWord = 
            v.includes('father') || v.includes('mother') || v.includes('parent') || v.includes('guardian') ||
            v.includes('spouse') || v.includes('wife') || v.includes('husband') || v.includes('self') || v.includes('head') ||
            v.includes('പിതാവ്') || v.includes('പിതാ') || v.includes('അച്ഛൻ') || v.includes('ഉപ്പ') ||
            v.includes('മാതാവ്') || v.includes('മാതാ') || v.includes('അമ്മ') || v.includes('ഉമ്മ') ||
            v.includes('ഭാര്യ') || v.includes('ഭർത്താവ്') || v.includes('രക്ഷിതാവ്') || v.includes('രക്ഷിതാ') ||
            v.includes('രക്ഷിതാവിന്റെ') || v.includes('നാഥൻ') || v.includes('നാഥ') || v.includes('സ്വയം');
            
          if (isParentOrSpouseWord) {
            const k = key.toLowerCase();
            if (
              k.includes('relation') || k.includes('ബന്ധം') || k.includes('head') || 
              k.includes('status') || k.includes('role') || k.includes('type') ||
              v === 'father' || v === 'mother' || v === 'spouse' || v === 'wife' || v === 'husband' || v === 'self' || v === 'head' ||
              v === 'പിതാവ്' || v === 'മാതാവ്' || v === 'ഭാര്യ' || v === 'ഭർത്താവ്' || v === 'സ്വയം' || v === 'നാഥൻ'
            ) {
              return true;
            }
          }
        }
      }
      return false;
    };

    // 1. Get flat data (Section A usually)
    const flatData = Object.keys(formData).reduce<Record<string, any>>((acc, key) => {
      if (['id', 'submittedBy', 'submittedAt', 'paymentStatus', 'paymentAmount', 'paidBy', 'paidAt'].includes(key)) return acc;
      const val = formData[key];
      if (Array.isArray(val)) return acc;
      if (typeof val === 'object' && val !== null) return { ...acc, ...val };
      return { ...acc, [key]: val };
    }, {});

    // Extract the student's name robustly
    let studentName = getMemberName(flatData, questions) || 'Unnamed Student';

    // Process the primary student under the exact same strict criteria
    const isPrimaryAdult = isAdultOrNonStudent(flatData);
    const primaryAge = getMemberAge(flatData, questions);
    const primaryCategory = getCategoryForAge(primaryAge);
    
    // Prevent hobby and sports strings being treated as student names
    const primaryNameLower = studentName.toLowerCase();
    const primaryIsHobbyWord = primaryNameLower.includes('hobby') || primaryNameLower.includes('hobbies') || primaryNameLower.includes('വിനോദം') || primaryNameLower.includes('sports') || primaryNameLower.includes('art') || primaryNameLower.includes('football') || primaryNameLower.includes('cricket') || primaryNameLower.includes('reading') || primaryNameLower.includes('drawing');
    const isPrimaryHobby = primaryIsHobbyWord && !primaryNameLower.includes('name') && !primaryNameLower.includes('പേര്');

    if (studentName && !isPrimaryAdult && primaryCategory && !isPrimaryHobby) {
      allStudents.push({
        ...flatData,
        id: 'primary-student',
        studentName: studentName, // Force it for display
        serialNo: flatData.serialNo || flatData.idNo || 'PND'
      });
    }

    // Check repeaters (additional students registered in repeater sections)
    const repeaterKeys = Object.keys(formData).filter(key => Array.isArray(formData[key]));
    let repeaterMembers: any[] = [];
    for (const key of repeaterKeys) {
      repeaterMembers = [...repeaterMembers, ...(formData[key] || [])];
    }

    repeaterMembers.forEach((m: any, idx: number) => {
      const name = getMemberName(m, questions);
      if (!name) return;

      // 1. Skip parents, guardians, spouses, and other adults
      if (isAdultOrNonStudent(m)) return;

      // 2. Strict student age category check (ages 4 to 21)
      const age = getMemberAge(m, questions);
      const category = getCategoryForAge(age);
      if (!category) {
        // Skip members that don't fit into standard student categories
        return;
      }

      // 3. Prevent hobby and sports strings being treated as student names
      const nameLower = name.toLowerCase();
      const isHobbyWord = nameLower.includes('hobby') || nameLower.includes('hobbies') || nameLower.includes('വിനോദം') || nameLower.includes('sports') || nameLower.includes('art') || nameLower.includes('football') || nameLower.includes('cricket') || nameLower.includes('reading') || nameLower.includes('drawing');
      if (isHobbyWord && !nameLower.includes('name') && !nameLower.includes('പേര്')) {
        return;
      }

      allStudents.push({
        ...m,
        id: `repeater-student-${idx}`,
        studentName: name, // Force it for display
        serialNo: m.serialNo || m.idNo || flatData.serialNo || 'PND'
      });
    });

    return allStudents;
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto w-full px-4 py-12 text-center">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-emerald-50">
          <div className="w-24 h-24 bg-emerald-150 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
            <Check size={48} className="text-emerald-600" />
          </div>
          <h2 className="text-4xl font-black text-emerald-900 mb-4 tracking-tight">Thank you!</h2>
          <p className="text-xl font-bold text-gray-700 mb-8">You submitted the form successfully.</p>
          
          <div className="bg-emerald-50 rounded-2xl p-6 text-left space-y-4 border border-emerald-150 shadow-inner">
            <p className="font-black text-emerald-800 uppercase tracking-widest text-sm border-b border-emerald-150 pb-2">Note :-</p>
            <ul className="space-y-3 text-sm font-bold text-emerald-900">
              <li className="flex gap-3">
                <span className="text-emerald-600">•</span>
                <span>Camp will be conducted at Malayalee Samajam premises</span>
              </li>
              <li className="flex gap-3">
                <span className="text-emerald-600">•</span>
                <span>Registration Fees: 200 AED / Student (150 AED for Samajam Members) to be paid on or before 9th July 2026 (09-07-2026)</span>
              </li>
              <li className="flex gap-3">
                <span className="text-emerald-600">•</span>
                <span>Transportation charges will be paid separately</span>
              </li>
              <li className="flex gap-3">
                <span className="text-emerald-600">•</span>
                <span>Please ensure students bring a light snack</span>
              </li>
            </ul>
            <div className="mt-6 pt-4 border-t border-emerald-150">
              <p className="text-amber-700 font-black">Note: Member has special discount</p>
              <p className="text-emerald-800 font-black mt-2">*More info contact - 02 64 24 488</p>
            </div>
          </div>
          
          <button 
            onClick={onSuccess}
            className="mt-10 w-full bg-emerald-800 hover:bg-emerald-900 text-white py-4 rounded-xl font-black text-lg transition-all active:scale-95 shadow-lg shadow-emerald-900/20"
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  }

  if (initLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-4 w-full">
        <div className="text-gray-500 font-medium animate-pulse">Loading...</div>
      </div>
    );
  }

  const currentSection = sections[step];
  const isLastStep = step === sections.length;
  const isLastDataStep = step === sections.length - 1;

  return (
    <div className="max-w-4xl mx-auto w-full">
      {step === 0 && (
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8 text-center border border-emerald-150 overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-2 bg-emerald-600"></div>
          <h1 className="text-3xl md:text-4xl font-black text-emerald-900 mb-2 tracking-tight">
            Malayalee Samajam Summer Camp - Insight 2026
          </h1>
          <p className="text-gray-500 font-bold text-sm uppercase tracking-widest">Official Registration Form</p>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 sticky top-0 z-20 border-b border-gray-150 w-full overflow-x-auto">
         <div className="flex justify-between items-center min-w-[300px]">
            <h2 className="text-xl font-bold text-emerald-800 truncate mr-2">
               {isLastStep ? 'Preview & ID Card' : currentSection?.label}
            </h2>
            <div className="flex space-x-1 flex-shrink-0">
               {[...sections, {id: 'preview'}].map((_, i) => (
                 <div key={i} className={`h-2 w-4 md:w-6 rounded-full transition-all ${i === step ? 'bg-emerald-600' : 'bg-gray-150'}`}/>
               ))}
            </div>
         </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 pb-32 w-full">
         {!isLastStep && currentSection && (
            <>
              {currentSection.isRepeater ? (
                <div className="space-y-8 w-full">
                    {(formData[currentSection.id] || []).map((item: any, idx: number) => (
                      <div key={item.id || idx} className="border-2 border-emerald-150 rounded-xl p-6 bg-white relative w-full">
                        <div className="flex justify-between mb-4 border-b pb-2">
                            <h4 className="font-bold text-emerald-800 flex items-center"><UserIcon className="mr-2"/> {getEntryTitle(idx, currentSection.id)}</h4>
                            <button type="button" onClick={() => removeRepeaterItem(currentSection.id, idx)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2/></button>
                        </div>
                        {renderQuestions(currentSection.id, item, (k, v) => updateRepeaterItem(currentSection.id, idx, k, v))}
                      </div>
                    ))}
                    <button type="button" onClick={() => addRepeaterItem(currentSection.id)} className="w-full py-4 border-2 border-dashed border-emerald-300 text-emerald-700 rounded-lg font-bold flex justify-center items-center hover:bg-emerald-50">
                      <Plus className="mr-2"/> Add Entry
                    </button>
                </div>
              ) : (
                renderQuestions(currentSection.id, formData[currentSection.id] || {}, (k, v) => updateSectionData(currentSection.id, k, v))
              )}
            </>
         )}

         {isLastStep && (
            <>
              <div className="flex flex-col items-center w-full space-y-6">
                 <style>{PRINT_STYLES}</style>
                 <div id="registration-receipt" className="bg-white border border-gray-150 rounded-lg p-8 shadow-sm w-full max-w-[80mm] mx-auto text-black font-sans leading-normal">
                    <div className="text-center border-b-[3px] border-black pb-4 mb-4">
                      <h3 className="text-2xl font-black uppercase tracking-tighter leading-none mb-1">Malayalee Samajam</h3>
                      <p className="text-base font-black uppercase tracking-widest text-gray-800">Summer Camp 2026</p>
                      <p className="text-[11px] mt-2 font-bold leading-tight text-gray-600">Tel: 02 642 4488</p>
                      <div className="mt-4 pt-3 border-t border-dashed border-gray-300">
                        <p className="text-[10px] font-black uppercase text-gray-400">Registration Staff: <span className="text-gray-900">{user.name || 'Staff'}</span></p>
                        <p className="text-[9px] font-bold text-gray-400 font-mono">REF: {formData['id'] || 'NEW'} | {new Date().toLocaleTimeString()}</p>
                      </div>
                    </div>

                    <div className="mb-6 py-2 bg-gray-50 rounded flex justify-center">
                      <div className="text-center font-mono text-xl border-2 border-black p-2 mt-2">
                        {getHouseholdSerialNo(formData) ? formatSerialNoForDisplay(getHouseholdSerialNo(formData)) : (formData['id'] || 'REGISTER')}
                      </div>
                    </div>

                    <div className="flex justify-between items-end mb-6 border-b-2 border-gray-150 pb-3">
                      <div className="text-left">
                        <p className="text-[11px] font-black text-gray-500 uppercase tracking-tighter">Serial Number</p>
                        <p className="text-lg font-black leading-none">{getHouseholdSerialNo(formData) ? formatSerialNoForDisplay(getHouseholdSerialNo(formData)) : 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[11px] font-black text-gray-500 uppercase tracking-tighter">Date</p>
                        <p className="text-xs font-black">{new Date().toLocaleDateString()}</p>
                      </div>
                    </div>
                 
                    <div className="space-y-3 mb-6 text-sm">
                      <div className="flex justify-between items-baseline gap-2">
                        <span className="font-black text-gray-500 uppercase text-[10px] whitespace-nowrap">Guardian</span>
                        <span className="font-black text-right uppercase leading-tight">{getHouseholdName(formData, questions) || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between items-baseline">
                        <span className="font-black text-gray-500 uppercase text-[10px]">Phone</span>
                        <span className="font-black">{getHouseholdPhone(formData, questions) || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between items-baseline">
                        <span className="font-black text-gray-500 uppercase text-[10px]">Samajam Member</span>
                        <span className="font-black uppercase">{getSurveyIICStatus(formData, questions, sections) ? 'YES' : 'NO'}</span>
                      </div>
                      {getSurveyIICStatus(formData, questions, sections) && (
                        <div className="flex justify-between border-t border-dotted border-gray-400 pt-2 mt-1">
                          <span className="font-black text-gray-500 uppercase text-[10px]">Member ID</span>
                          <span className="font-black uppercase">{getSurveyIICId(formData, questions) || 'Verified'}</span>
                        </div>
                      )}
                    </div>
                 
                    <div className="border-t-[3px] border-black pt-3 mb-6">
                      <p className="text-xs font-black uppercase tracking-widest mb-3 border-b-2 border-gray-150 pb-2">Registration Items</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b-2 border-gray-150">
                            <th className="text-left py-2 uppercase font-black">Description</th>
                            <th className="text-right py-2 uppercase font-black">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-150">
                          {getQualifyingMembers().map((m: any, idx: number) => {
                            const studentName = m.studentName || getMemberName(m, questions) || `Student ${idx + 1}`;
                            const age = getMemberAge(m, questions);
                            const category = getCategoryForAge(age);
                            const isMember = getSurveyIICStatus(formData, questions, sections);
                            const entryFee = isMember ? 150 : 200;
                            return (
                              <tr key={`${m.id || 'student'}-${idx}`}>
                                <td className="py-3 pr-2">
                                  <p className="font-black leading-tight uppercase text-sm">{idx + 1}. {studentName}</p>
                                  <p className="text-[10px] text-emerald-700 font-black uppercase">ID: {m.serialNo || m.idNo || 'PND'}</p>
                                  <p className="text-[9px] text-gray-400 font-bold uppercase mt-0.5">{category ? getCategoryName(category) : 'Camp Registration Fee'}</p>
                                </td>
                                <td className="py-3 text-right font-black text-sm">{entryFee.toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="border-t-[3px] border-black pt-4 space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="font-black uppercase text-gray-500">Subtotal</span>
                        <span className="font-black">{(getQualifyingMembers().length * 200).toFixed(2)} AED</span>
                      </div>
                      {getSurveyIICStatus(formData, questions, sections) && (
                        <div className="flex justify-between text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1.5 rounded border border-emerald-150 font-black">
                          <span className="uppercase italic text-[10px]">Samajam Member Special Rate Applied</span>
                          <span className="text-[10px]">-50.00 AED / student</span>
                        </div>
                      )}
                      <div className="flex justify-between text-xl font-black border-t-[3px] border-double border-black pt-3 mt-3">
                        <span className="uppercase tracking-tighter">Total Due</span>
                        <span>
                          {(getQualifyingMembers().length * (getSurveyIICStatus(formData, questions, sections) ? 150 : 200)).toFixed(2)} AED
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-10 pt-6 border-t-2 border-dashed border-gray-300 text-center">
                       <p className="text-[11px] font-black text-gray-800 uppercase leading-snug tracking-tight">
                         This is a registration summary.<br/>
                         Please complete payment at counter.
                       </p>
                    </div>
                  </div>

                  <div className="flex gap-4 w-full max-w-lg no-print">
                     <button 
                       type="button"
                       onClick={() => window.print()}
                       className="flex-1 bg-white border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 font-black py-3 rounded-2xl flex items-center justify-center gap-2 transition-all shadow"
                     >
                       <Printer size={20} />
                       Print Summary
                     </button>
                  </div>
               </div>

              {cardTemplate ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl justify-items-center">
                  {getQualifyingMembers().length > 0 ? (
                    getQualifyingMembers().map((member: any, idx: number) => {
                      const parentData = Object.values(formData).reduce<Record<string, any>>((acc, curr) => {
                         if (typeof curr === 'object' && curr !== null && !Array.isArray(curr)) {
                             return { ...acc, ...(curr as Record<string, any>) };
                         }
                         return acc;
                      }, {});
                      
                      const studentPhoto = getMemberPhoto(member, questions);
                      const parentPhoto = getMemberPhoto(parentData, questions);

                      return (
                        <MemberCardCanvas
                          key={member.id || idx}
                          cardTemplate={cardTemplate}
                          member={{
                            ...member,
                            housePhoto: studentPhoto || parentPhoto || member.housePhoto
                          }}
                          parentData={parentData}
                          index={idx}
                          questions={questions}
                          onDownload={() => setHasDownloaded(true)}
                        />
                      );
                    })
                  ) : (
                    <div className="space-y-4 w-full max-w-md md:col-span-2 flex flex-col items-center">
                      <p className="text-sm text-gray-500 italic text-center mb-2">No family members registered. Showing Household ID Card preview:</p>
                      <canvas ref={canvasRef} className="w-full h-auto shadow-lg rounded bg-white border" />
                      <button type="button" onClick={handleDownloadCard} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg flex justify-center items-center"><Download className="mr-2"/> Download ID Card</button>
                    </div>
                  )}
                </div>
              ) : <div className="p-4 bg-yellow-50 text-yellow-800 rounded flex flex-col items-center text-center">
                  <p className="font-bold mb-2">No ID Card Template Configured</p>
                  <p className="text-sm">Please contact the Master Admin to upload a template in the 'ID Cards' section.</p>
                  <p className="text-xs text-gray-400 mt-1">You can still submit the survey without generating a card.</p>
              </div>}
            </>
         )}

         <div className={`fixed bottom-0 left-0 right-0 bg-white border-t z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] pb-[env(safe-area-inset-bottom)] ${user.id !== 'guest' ? 'md:left-64' : ''}`}>
            <div className="max-w-4xl mx-auto w-full flex justify-between items-center p-3 sm:p-4">
               {step > 0 ? (
                  <button type="button" onClick={prevStep} className="px-4 py-2 sm:px-6 sm:py-2.5 bg-gray-150 text-gray-700 rounded-lg hover:bg-gray-150 border font-medium flex items-center transition-colors text-sm sm:text-base">
                     <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2"/> 
                     <span className="hidden sm:inline">Back</span>
                     <span className="sm:hidden">Prev</span>
                  </button>
               ) : <div/>}
               
               {!isLastStep ? (
                  <div className="flex space-x-2">
                     <button 
                       type="button" 
                       onClick={nextStep} 
                       disabled={isMediaProcessing}
                       className={`px-6 py-2.5 sm:px-8 sm:py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-md font-bold flex items-center transition-all text-sm sm:text-base ${isMediaProcessing ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
                     >
                       {isMediaProcessing ? 'Processing...' : (isLastDataStep ? 'Preview' : 'Next Step')} 
                       {!isMediaProcessing && <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1 sm:ml-2"/>}
                     </button>
                  </div>
               ) : (
                  <button 
                   type="submit" 
                   disabled={loading || isMediaProcessing} 
                   className={`px-6 py-2.5 sm:px-8 sm:py-3 text-white rounded-lg shadow-md font-bold flex items-center transition-all text-sm sm:text-base ${loading || isMediaProcessing ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-800 hover:bg-emerald-900 active:scale-95'}`}
                  >
                    {loading ? <Loader className="animate-spin w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" /> : <Save className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2"/>} 
                    Submit
                  </button>
               )}
            </div>
         </div>
      </form>
    </div>
  );
};

export default SurveyForm;
